/**
 * YouTube utility functions for BB-Native
 */

/**
 * Extract YouTube video ID from various URL formats
 * Supports:
 *  - https://www.youtube.com/watch?v=VIDEO_ID
 *  - https://youtu.be/VIDEO_ID
 *  - https://www.youtube.com/embed/VIDEO_ID
 *  - https://www.youtube.com/v/VIDEO_ID
 *  - https://www.youtube.com/shorts/VIDEO_ID
 *  - Plain video ID string
 */
export function extractVideoId(url: string): string | null {
  if (!url) return null;

  // Already a plain video ID (11 chars, alphanumeric + dash/underscore)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.+&v=)([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * Generate YouTube embed URL with optional parameters
 */
export function getEmbedUrl(
  videoId: string,
  opts: {
    autoplay?: boolean;
    mute?: boolean;
    loop?: boolean;
    controls?: boolean;
    modestBranding?: boolean;
    /** Disable keyboard shortcuts (prevents accidental seek/pause via keyboard) */
    disableKb?: boolean;
    /** Hide the fullscreen button */
    noFullscreen?: boolean;
    /** Hide closed captions button */
    noCaptions?: boolean;
    /** origin param for postMessage security */
    origin?: string;
  } = {}
): string {
  const {
    autoplay = false,
    mute = false,
    loop = false,
    controls = true,
    modestBranding = true,
    disableKb = false,
    noFullscreen = false,
    noCaptions = false,
    origin,
  } = opts;

  // Extract clean video ID in case a full URL was passed
  const cleanId = extractVideoId(videoId) || videoId;

  const params = new URLSearchParams();
  params.set('enablejsapi', '1');
  // Request highest quality — YouTube will serve best resolution the
  // connection supports, starting from hd1080 instead of auto-lowest.
  params.set('vq', 'hd1080');
  if (autoplay) params.set('autoplay', '1');
  if (mute) params.set('mute', '1');
  if (loop) {
    params.set('loop', '1');
    params.set('playlist', cleanId);
  }
  params.set('controls', controls ? '1' : '0');
  if (modestBranding) params.set('modestbranding', '1');
  params.set('iv_load_policy', '3');   // no video annotations
  params.set('showinfo', '0');
  params.set('rel', '0');              // no related videos at end
  params.set('playsinline', '1');      // inline on iOS
  if (disableKb) params.set('disablekb', '1');
  if (noFullscreen) params.set('fs', '0');
  if (noCaptions) params.set('cc_load_policy', '0');
  if (origin) params.set('origin', origin);

  return `https://www.youtube.com/embed/${cleanId}?${params.toString()}`;
}

/**
 * Generate YouTube thumbnail URL
 * Quality options: default, mqdefault, hqdefault, sddefault, maxresdefault
 */
export function getThumbnailUrl(
  videoId: string,
  quality: 'default' | 'mqdefault' | 'hqdefault' | 'sddefault' | 'maxresdefault' = 'hqdefault'
): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Format duration in milliseconds to human readable string
 * e.g., 245000 -> "4:05"
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get YouTube watch URL from video ID
 */
export function getWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}
